import { Link } from 'react-router-dom'
import { Phone, Calendar } from 'lucide-react'
import { HERO, SITE } from '../content/siteContent'

export default function Hero() {
  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-brand-navy via-brand-blue to-brand-sky"
      aria-label="Welcome to Grace Pointe Child Development Center"
    >
      {/* Decorative background shapes */}
      <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
        <div className="absolute -top-1/4 -right-1/4 w-[600px] h-[600px] rounded-full bg-white/5 blur-3xl" />
        <div className="absolute -bottom-1/3 -left-1/4 w-[500px] h-[500px] rounded-full bg-brand-amber/10 blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-brand-sky/10 blur-3xl" />
      </div>

      {/* Subtle dot-grid overlay */}
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '32px 32px' }}
        aria-hidden="true"
      />

      <div className="section-container relative z-10 py-24 lg:py-32">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">

          {/* Text block */}
          <div className="text-center lg:text-left animate-fade-in-up">
            {/* Eyebrow badge */}
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-6 backdrop-blur-sm">
              <span className="w-2 h-2 rounded-full bg-brand-amber animate-pulse" aria-hidden="true" />
              <span className="text-white text-xs font-round font-semibold tracking-wide">
                Enrolling for 2025–2026 School Year
              </span>
            </div>

            {/* Headline */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-white leading-[1.1] mb-6">
              {HERO.headline.split('\n').map((line, i) => (
                <span key={i}>
                  {i === 1 ? (
                    <span className="text-brand-amber">{line}</span>
                  ) : (
                    line
                  )}
                  {i < HERO.headline.split('\n').length - 1 && <br />}
                </span>
              ))}
            </h1>

            {/* Subheadline */}
            <p className="text-base md:text-lg lg:text-xl text-slate-200 leading-relaxed mb-8 max-w-xl lg:max-w-none">
              {HERO.subheadline.split('\n').map((line, i) => (
                <span key={i}>
                  {line}
                  {i < HERO.subheadline.split('\n').length - 1 && <br />}
                </span>
              ))}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start mb-8">
              <Link to={HERO.cta1.href} className="btn-gold px-8 py-4 text-base">
                <Calendar size={18} aria-hidden="true" />
                {HERO.cta1.label}
              </Link>
              <a href={`tel:${SITE.contact.phone.replace(/\D/g,'')}`} className="btn-secondary px-8 py-4 text-base text-brand-navy border-white/40 bg-white/10 text-white hover:bg-white/20">
                <Phone size={18} aria-hidden="true" />
                {SITE.contact.phone}
              </a>
            </div>

            {/* Trust line */}
            <p className="text-sm text-slate-300 font-round">
              📍 {HERO.trustLine}
            </p>
          </div>

          {/* Image mosaic */}
          <div className="relative hidden lg:block animate-fade-in" aria-hidden="true">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <div className="rounded-xl3 overflow-hidden shadow-lift aspect-square bg-white/10">
                  <img
                    src="/images/IMG_7384.jpg"
                    alt="Children learning at Grace Pointe"
                    className="w-full h-full object-cover opacity-90 hover:opacity-100 hover:scale-105 transition-all duration-500"
                    loading="eager"
                  />
                </div>
                <div className="rounded-xl3 overflow-hidden shadow-lift aspect-video bg-white/10">
                  <img
                    src="/images/IMG_7413.jpg"
                    alt="Happy children at Grace Pointe CDC"
                    className="w-full h-full object-cover opacity-90 hover:opacity-100 hover:scale-105 transition-all duration-500"
                    loading="eager"
                  />
                </div>
              </div>
              <div className="space-y-4 mt-8">
                <div className="rounded-xl3 overflow-hidden shadow-lift aspect-video bg-white/10">
                  <img
                    src="/images/IMG_7576.jpg"
                    alt="Classroom activities"
                    className="w-full h-full object-cover opacity-90 hover:opacity-100 hover:scale-105 transition-all duration-500"
                    loading="eager"
                  />
                </div>
                <div className="rounded-xl3 overflow-hidden shadow-lift aspect-square bg-white/10">
                  <img
                    src="/images/IMG_7625.jpg"
                    alt="Grace Pointe children playing"
                    className="w-full h-full object-cover opacity-90 hover:opacity-100 hover:scale-105 transition-all duration-500"
                    loading="eager"
                  />
                </div>
              </div>
            </div>

            {/* Floating stat cards */}
            <div className="absolute -bottom-4 -left-6 bg-white rounded-xl2 shadow-lift p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-brand-cream rounded-xl flex items-center justify-center text-xl">🏫</div>
              <div>
                <p className="text-brand-navy font-bold text-sm">Avon, Indiana</p>
                <p className="text-slate-500 text-xs">Locally rooted community</p>
              </div>
            </div>
            <div className="absolute -top-4 -right-4 bg-brand-amber rounded-xl2 shadow-lift p-4 text-center">
              <p className="text-white font-bold text-lg leading-none">7AM</p>
              <p className="text-white/80 text-xs font-round">Open Early</p>
            </div>
          </div>
        </div>
      </div>

      {/* Wave divider */}
      <div className="absolute bottom-0 left-0 right-0" aria-hidden="true">
        <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full" preserveAspectRatio="none">
          <path d="M0 80V40C240 0 480 60 720 40C960 20 1200 60 1440 40V80H0Z" fill="white" />
        </svg>
      </div>
    </section>
  )
}
